import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddLinkRounded from '@mui/icons-material/AddLinkRounded';
import MailRounded from '@mui/icons-material/MailRounded';
import {
  EmptyState, ListTable, Panel, StatusChip, ToolbarButton, type ListColumn, type StatusTone,
} from '../../../ui-kit';
import { fetchStlIntegrations, type StlIntegration } from '../../../services/speedToLeadService';
import { fromNow, useLive } from '../rocco/live';
import { AgencyZoomMark, TwilioMark } from '../missed-call/logos';
import { ElevenLabsMark, HubSpotMark } from './logos';

function stateOf(row: StlIntegration): { tone: StatusTone; label: string } {
  if (!row.connected) return { tone: 'neutral', label: 'Not connected' };
  if (row.error || row.job_error) return { tone: 'warning', label: 'Needs attention' };
  return { tone: 'good', label: 'Connected' };
}

const LOGOS: Record<StlIntegration['id'], React.ReactNode> = {
  hubspot: <HubSpotMark />,
  voice: <ElevenLabsMark />,
  transfer: <TwilioMark />,
  agencyzoom: <AgencyZoomMark />,
  email: <MailRounded sx={(t) => ({ fontSize: 22, color: t.tokens.brand.primary })} />,
};

/** Connectors added on the Connectors page; the others are set by us on the server. */
const CONNECT: Partial<Record<StlIntegration['id'], string>> = { hubspot: 'Connect HubSpot', agencyzoom: 'Connect AgencyZoom' };

/**
 * What Speed to Lead is joined to, as it is right now: HubSpot for new leads,
 * the AI caller, the transfer line that rings the specialist, AgencyZoom for
 * the bookings, and email.
 */
export default function IntegrationsPage() {
  const navigate = useNavigate();
  const { data, error, loading } = useLive(fetchStlIntegrations, 30_000);
  if (error && !data) return <EmptyState message={error} />;

  const columns: ListColumn<StlIntegration>[] = [
    {
      key: 'name', header: 'System', grow: 1.2, minWidth: 220,
      render: (row) => (
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box sx={{ display: 'flex', flexShrink: 0 }}>{LOGOS[row.id]}</Box>
          <Stack sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{row.name}</Typography>
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, overflowWrap: 'anywhere' })}>{row.detail}</Typography>
          </Stack>
        </Stack>
      ),
    },
    {
      key: 'role', header: 'Does', width: 200,
      render: (row) => <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{row.role}</Typography>,
    },
    {
      key: 'state', header: 'State', width: 200,
      render: (row) => (
        CONNECT[row.id] && !row.connected ? (
          <ToolbarButton variant="outlined" icon={<AddLinkRounded />} onClick={() => navigate('/connectors')}>
            {CONNECT[row.id]}
          </ToolbarButton>
        ) : <StatusChip {...stateOf(row)} />
      ),
    },
    {
      key: 'activity', header: 'Recently', grow: 2, minWidth: 240,
      render: (row) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>
            {[row.activity, row.last_run_at ? `last checked ${fromNow(row.last_run_at)}` : null].filter(Boolean).join(' · ')}
          </Typography>
          {row.error || row.job_error ? (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.danger, overflowWrap: 'anywhere' })}>
              {row.error || row.job_error}
            </Typography>
          ) : null}
        </Stack>
      ),
    },
  ];

  return (
    <Panel title="Connected systems" subtitle="New leads in, calls out, hand-overs and bookings · as they are now">
      <ListTable columns={columns} rows={data?.integrations ?? []} getRowKey={(row) => row.id} loading={loading}
        emptyMessage="Nothing connected." />
    </Panel>
  );
}
